import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import {
  useBingoPurchaseMutation,
  useGetBingoEventQuery,
} from '@/api/queries/bingo';
import { useGetMyInfoMutation } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { opacityAnimation } from '@/common/config/motion-animations';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { openTgInvoice } from '@/common/lib/openTgInvoice';
import { Playdeck, SendAnalitics } from '@/common/lib/playdeck';
import { type BuyDiceForTonPaymentResponseType } from '@/common/types/payment';
import { LightColorEffect } from '@/components/common/LightColorEffect/LightColorEffect';
import { ModalContext } from '@/components/Modals/Modal/config';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import {
  changePaymentStatus,
  clearPendingPurchase,
  setPendingPurchase,
} from '@/redux/reducers/paymentsReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { BuyBingoBallsContext } from '../context';

import css from './styles.module.scss';

const ConfirmBuy: FC = () => {
  const { t } = useTranslation();
  const [purchase, { isLoading }] = useBingoPurchaseMutation();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const [getMyInfo] = useGetMyInfoMutation();
  const { activeOfferIndex, source } = useContext(BuyBingoBallsContext);
  const { secondModal, thirdModal, closeModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const { data: event } = useGetBingoEventQuery();
  const packages = event?.packages;
  const ballsBalance = useAppSelector(
    (state) => state.user.user?.balance?.bingoBalls ?? 0,
  );

  const activePackage = packages?.[activeOfferIndex];

  const buildAnaliticsPayload = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): {
    item: 'bingo_balls';
    item_id: string;
    price: number;
    currency: 'ton' | 'xtr' | 'usdt';
  } | null => {
    if (!activePackage) return null;

    return {
      item: 'bingo_balls',
      item_id: activePackage.ballCount.toString(),
      price: activePackage.amountUsd,
      currency,
    };
  };

  const buildExtras = (
    currency: 'ton' | 'xtr' | 'usdt',
  ): Record<string, unknown> | null => {
    if (!activePackage) return null;

    return {
      ball_count: activePackage.ballCount,
      price: activePackage.amountUsd,
      currency,
      source: source ?? 'unknown',
      balls_balance: ballsBalance,
      daily_contract_balls: event?.dailyContractBalls ?? 0,
      daily_contract_balls_limit: event?.dailyContractBallsLimit ?? 0,
    };
  };

  const markPaymentPending = (currency: 'ton' | 'xtr'): void => {
    const payload = buildAnaliticsPayload(currency);
    const extras = buildExtras(currency);

    if (!payload || !extras) return;

    dispatch(setPendingPurchase({ ...payload, extras }));
    SendAnalitics.purchasePending(payload);
  };

  const sendUsdtSuccessAnalitics = (): void => {
    const payload = buildAnaliticsPayload('usdt');
    const extras = buildExtras('usdt');

    if (!payload || !extras) return;

    SendAnalitics.purchaseSuccessfull(payload);
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'bingo_purchase_completed',
      user_properties: {},
      event_properties: extras,
    });
  };

  const buyBalls = async (currency: 'XTR' | 'TON'): Promise<void> => {
    try {
      if (!activePackage) return;

      const currencyLower = currency.toLowerCase() as 'xtr' | 'ton';
      const body = {
        ballCount: activePackage.ballCount,
        currency: currencyLower,
      };

      if (currency === 'TON') {
        connectWalletAndDoTransaction(
          async () =>
            (await purchase(
              body,
            ).unwrap()) as unknown as BuyDiceForTonPaymentResponseType,
          {
            onSuccess: () => {
              ToastNotification(t('Payment will be credited soon.'));
              markPaymentPending('ton');
            },
            onError: () => {
              ToastNotification(t('Something went wrong, please try again'));
            },
          },
        );

        return;
      } else {
        const response = await purchase(body).unwrap();

        if (response.link) {
          openTgInvoice(response.link, {
            onSuccess: () => {
              markPaymentPending('xtr');
            },
            playdeckRequestPayload: {
              amount: activePackage.amountStars,
              description: 'buy bingo balls',
              externalId: `buy-bingo-balls-${activePackage.ballCount}`,
            },
          });

          return;
        }
      }
    } catch (error) {
      console.error(error);
      showToast(t('Something went wrong, please try again'));
    }
  };

  const buyUsdt = async (): Promise<void> => {
    if (!activePackage) return;

    try {
      await purchase({
        ballCount: activePackage.ballCount,
        currency: 'usdt',
      }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      await getMyInfo();
      dispatch(changePaymentStatus('success'));
      sendUsdtSuccessAnalitics();
      ToastNotification(t('Success. Bingo balls credited and ready to use'));
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'));
    }
  };

  const onClick = (currency: 'XTR' | 'TON'): void => {
    if (activePackage) {
      SendAnalitics.purchaseAttempt({
        item: 'bingo_balls',
        item_id: activePackage.ballCount.toString(),
        price: activePackage.amountUsd,
        currency,
      });
      Playdeck.sendAnalitics({
        type: 'custom_event',
        name: 'bingo_purchase_started',
        user_properties: {},
        event_properties: {
          ball_count: activePackage.ballCount,
          price: activePackage.amountUsd,
          currency,
          source: source ?? 'unknown',
          balls_balance: ballsBalance,
          daily_contract_balls: event?.dailyContractBalls ?? 0,
          daily_contract_balls_limit: event?.dailyContractBallsLimit ?? 0,
        },
      });
    }

    buyBalls(currency)
      .then(() => {
        dispatch(changePaymentStatus('pending'));
      })
      .catch(() => {
        dispatch(changePaymentStatus(undefined));
        dispatch(clearPendingPurchase());
      });
  };

  return (
    activePackage && (
      <motion.div
        {...opacityAnimation}
        className="w-full flex flex-col items-center"
      >
        <div className="relative w-24 h-24 flex items-center justify-center">
          <img src="/img/bingo/bingo-icon.png" alt="" className={css.ball} />
          <LightColorEffect
            lightClassName={css.purpleGradient}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[85.04%] -z-[1]"
          />
        </div>
        <div className="flex flex-col items-center w-full relative z-[2] pt-4">
          <span className="font-black text-[2.5rem] leading-none mb-3">
            +{activePackage.ballCount}
          </span>
          <div className="w-full mb-[1.875rem] mt-10">
            <TONandStarsBuyButtons
              onClick={onClick}
              isLoading={isLoading}
              appleUsd={activePackage.amountUsd}
              price={multiplication(
                activePackage.amountUsd,
                additionalDiscountForUsdt.coefficient,
              )}
              discount={additionalDiscountForUsdt.percentage}
              starPrice={activePackage.amountStars}
              mustBeUsdtButton={true}
              onUsdtBuy={buyUsdt}
              variant="switch"
              classNames="!px-0"
            />
            <GreyBlurButton
              className="mt-3"
              text={t('Close')}
              onClick={() => {
                closeModal();
              }}
            />
          </div>
        </div>
      </motion.div>
    )
  );
};

export { ConfirmBuy };
